import { dispatch, actions, stateRef } from './states'
import { jwtDecode } from './utils'
import { tsgUserManagers, clearCurrentTsgId, getTsgAccess as getTsgAccessFn } from './tsg'
import { // only pick some from utils, not expose everything
  doLogin,
  doLogout,
  getAuthHeaders,
  getCSRFToken,
  getMd5Salt,
  getParam,
  getSession,
  getRedirectURL,
  getAdminClusterURL,
  getFawkesTenantId,
  checkIsFeatureEnabled,
  isValidRoute,
} from '../containers/main/ui-lib/utils'
import { fetchIamCustomRoleDefinitionAndCache } from './actions'
import { indexCustomRoles } from './access'
import cache from './cache'

import type { NavItem } from './microAppTypes'
import type { FeatureInfo, IamCustomRolesDefinition, ReadonlyAccess } from './stateTypes'

export type { History } from 'history'
export type { Pendo } from './pendo'
export type { StoreState, AuthState, MainState, GlobalStore, GlobalStore as Store, ParsedPrn, Authed, AuthInfo, FeatureInfo, FawkesTenantInfo, UserState, ConfigLocation, LoggedInUser, AppRegion, AppManifests, AppManifest, IamPermission, IamPermissions, IamRoleDefinition, StringArraySet, Vars, ReadonlyAccess, IndexedArray, MarketplaceContent, DeveloperDefinedField, SupportAccount } from './stateTypes'
export type { Instance, Tsg, ReadonlyTsgTree, TsgTree, ReadonlyTsgInstances } from './indexedArrays'
export type { AsyncCache, SyncCache, CacheOptions } from './cache'
export type { NavItem, NavAction, NavParams, MicroAppConfig, MicroApp, MicroAppExport, SparkySettings, AvailFunc, LazyFactory } from './microAppTypes'
export type { TsgLoadFnParam, TsgLoadFn } from './newMicroAppTypes'
export type { TokenManager, Entitlements, Credentials } from '@sparky/token-manager'

export const fawkesUtils = {
  doLogin,
  doLogout,
  getAuthHeaders,
  getCSRFToken,
  getMd5Salt,
  jwtDecode,
  getParam,
  getSession,
  getRedirectURL,
  getAdminClusterURL,
  getFawkesTenantId,
  checkIsFeatureEnabled,
  isValidRoute,
} as {
  doLogin: () => void,
  doLogout: () => void,
  getAuthHeaders: () => { 'x-csrf-token': string, 'x-auth-jwt': string },
  getCSRFToken: () => string,
  getMd5Salt: () => string,
  jwtDecode: typeof jwtDecode,
  getParam: (arg: string) => string | undefined,
  getSession: () => string | undefined,
  getRedirectURL: () => string,
  getAdminClusterURL: () => string,
  getFawkesTenantId: () => string | undefined,
  checkIsFeatureEnabled: (list?: FeatureInfo[], key?: string) => boolean,
  isValidRoute: (nav: NavItem) => boolean,
}

/**
 * @deprecated use fawkesUtils instead
 */
export { fawkesUtils as utils }

export const _resetAuth = (): void => {
  clearCurrentTsgId()
  actions.logout()
  setTimeout(() => {
    localStorage.clear()
    sessionStorage.clear()
  }, 100)
}

const tsgAccessPromises = {} as Record<string, Promise<ReadonlyAccess | undefined> | undefined>
export const getTsgAccess = (tsg_id: string) => {
  const existing = tsgAccessPromises[tsg_id]
  if (existing) { // prevent duplicated requests in a short time
    return existing
  }
  const fn = async () => {
    try {
      let definitions = stateRef.current.auth?.rolePermissionDefinition
      if (definitions && definitions.customRoles?.tsg_id !== tsg_id) {
        const cacheKey = `custom-roles-${tsg_id}`
        const cached = await cache.get<IamCustomRolesDefinition>(cacheKey)
        if (cached) {
          Object.assign(indexCustomRoles(cached), { tsg_id })
          definitions = { ...definitions, customRoles: cached }
        }
        else {
          const customRoles = await fetchIamCustomRoleDefinitionAndCache(tsg_id)
          definitions = { ...definitions, customRoles }
        }
      }
      const access = await getTsgAccessFn(tsg_id, definitions)
      return access
    }
    finally {
      delete tsgAccessPromises[tsg_id]
    }
  }
  const promise = fn()
  tsgAccessPromises[tsg_id] = promise
  return promise
}

Object.assign(dispatch, actions)

Object.defineProperties(tsgUserManagers, {
  getCurrentTsgId: {
    value: (): string | undefined => {
      return stateRef.current.auth?.tsg_id || tsgUserManagers.__current_tsg_id__
    }
  }
})

/**
 * @deprecated micro-app should handle its own translation
 */
export const _T = (str: string): string => str

export { __isDebugOn } from '../init/debug'
export { SPARKY_FRAMEWORK } from './constants'
export * as hooks from './hooks'
export * as layout from '../containers/layout'
export { store, dispatch, getState, getAuthState, getMainState, events, actions, history, onAuthed } from './states'
export { inited, authed, loaded, getInitSettings, getFrameworkVar, getFrameworkVars, getFrameworkServiceUrl, getCurrentPlatformRegion, isOidcClient, isAppCanBeLaunched } from './states'
export { prn, createUrnUtil } from './access'
export { hasMicroApp, hasExposedNamespace, getMicroAppVars, importExposed, lazyImportComponent, getExposedSync } from './microApps'
export { hasTsgSupport, getTsgToken, getCurrentTsgId, getCurrentTsgToken, revokeTsgToken } from './tsg'
export { indexTsgTree, indexTsgInstances, indexAppManifests, indexSupportAccounts } from './indexedArrays'
export { getDatadogRum, getPendo } from './externals'
export { GlobalStateContext, useStoreSelector } from '../containers/common/storeHook'
export { default as i18n, getGridLocale, getUserLanguage } from '../i18n'
export { default as StartupManager } from '../containers/main/StartupManager'
export { importLibs, moduleImport } from '../libs/base'
export { getAuthData, getToken, getTokenManager } from '../containers/main/TkmProvider'
export { default as AuthInfoSpinner } from '../containers/main/AuthInfoSpinner'
export { default as LoadingPlaceholder } from '../containers/common/LoadingPlaceholder'
export { loadJs, loadCss, clearLoadingMask, getRootElement, jwtDecode } from './utils'
export * from './typeUtils'
